import { Feature, FeatureCollection, Geometry, GeometryCollection, Point } from "geojson";
import { LngLatBounds } from "mapbox-gl";
import { bbox } from "@turf/bbox";
import { featureCollection, geometryCollection } from "@turf/helpers";
import { MarketExpansionCounty } from "@app/overviews/market-expansion-dashboard/shared/MarketExpansionCounty";
import {
  MAX_PROVIDER_CIRCLE_SIZE,
  MIN_PROVIDER_CIRCLE_SIZE,
  PROVIDER_FOOTPRINT_MAX_ZOOM_ON_POINT,
} from "@app/overviews/market-expansion-dashboard/MarketExpansionDashboardConstants";
import { ProviderFootprintFeatureProperties } from "@app/overviews/market-expansion-dashboard/shared/ProviderFootprintFeatureProperties";
import { ProviderFootprintRecord } from "@app/overviews/market-expansion-dashboard/shared/ProviderFootprintRecord";
import { resizeObserverManager } from "@app/shared/qes-standards/utility/ResizeObserverManager";

/** Parses a county's `geoJSON` string into a `FeatureCollection`, or null if it isn't valid JSON. */
const parseJSON = (json: string): FeatureCollection | null => {
  try {
    return JSON.parse(json) as FeatureCollection;
  } catch {
    return null;
  }
};

/** Merges each county's parsed `geoJSON` into a single FeatureCollection, attaching the county's own properties (SSA, name, metric value, etc.) to every feature it contributes. */
const convertToFeatureCollection = <T extends MarketExpansionCounty>(counties: T[]): FeatureCollection<Geometry, T> => {
  return featureCollection((counties
    ? counties
      .flatMap((county) =>
        parseJSON(county.geoJSON)
          ?.features.map((p) =>
            // We want everything except for the GeometryCollection as we know we are not going to receive a GeometryCollection.
            geometryCollection([p.geometry as Exclude<Geometry, GeometryCollection>],
              {
                ...county,
              }
            )))
    : []).filter(feature => !!feature))
};

/** Bounding box for a cluster of point features. A single point has no natural bounds, so it's padded to a small fixed-radius box around itself instead of returning a zero-size box. */
const getFeatureClusterBounds = (features: Feature[]): LngLatBounds | null => {
  if (features.length > 1) {
    const [minLong, minLat, maxLong, maxLat] = bbox(featureCollection(features));
    return new LngLatBounds([minLong, minLat], [maxLong, maxLat]);
  } else if (features.length === 1) {
    const [lng, lat] = (features[0].geometry as Point).coordinates;
    // 1 degree latitude ≈ 69 miles; 1 degree longitude ≈ 69 * cos(lat) miles
    const latDelta = PROVIDER_FOOTPRINT_MAX_ZOOM_ON_POINT / 69;
    const lngDelta = PROVIDER_FOOTPRINT_MAX_ZOOM_ON_POINT / (69 * Math.cos((lat * Math.PI) / 180));
    return new LngLatBounds([lng - lngDelta, lat - latDelta], [lng + lngDelta, lat + latDelta]);
  } else {
    return null;
  }
};

/** Scales a provider's circle radius linearly between the min/max sizes by its case count's percentile within the given range. */
const getProviderCircleRadius = (cases: number, minCases: number, maxCases: number): number => {
  if (maxCases === minCases) return MIN_PROVIDER_CIRCLE_SIZE;
  const percentile = (cases - minCases) / (maxCases - minCases);
  return (MAX_PROVIDER_CIRCLE_SIZE - MIN_PROVIDER_CIRCLE_SIZE) * percentile + MIN_PROVIDER_CIRCLE_SIZE;
};

/** Converts a provider footprint record into a GeoJSON Point feature carrying its map-rendering properties (color key, circle radius, case counts). */
const convertFootprintRecordToGeoJson = (
  record: ProviderFootprintRecord,
  serviceAreaCases: number,
  circleRadius: number,
  contractEntityColorKey: string,
): Feature<Point, ProviderFootprintFeatureProperties> => ({
  geometry: {
    coordinates: [record.longitude, record.latitude],
    type: "Point",
  },
  properties: {
    ccn: record.ccn,
    ccnTotalCases: record.ccnTotalCases,
    contractEntityColorKey,
    contractEntityName: record.contractEntityName,
    facilityName: record.facilityName,
    groupingId: record.groupingId,
    latitude: record.latitude,
    longitude: record.longitude,
    radius: circleRadius,
    serviceAreaCases,
  },
  type: "Feature",
});

/** Darken or lighten color by given factor (negative to darken, positive to lighten).*/
const adjustColorBrightness = (colorHex: string, factor: number): string => {
  if (factor <= -1) return "#000000";

  const r = parseInt(colorHex.substring(1, 3), 16);
  const g = parseInt(colorHex.substring(3, 5), 16);
  const b = parseInt(colorHex.substring(5, 7), 16);

  const rAdjusted = Math.round(r * (1 + factor));
  const gAdjusted = Math.round(g * (1 + factor));
  const bAdjusted = Math.round(b * (1 + factor));

  const rHex = Math.min(rAdjusted, 255).toString(16).padStart(2, "0");
  const gHex = Math.min(gAdjusted, 255).toString(16).padStart(2, "0");
  const bHex = Math.min(bAdjusted, 255).toString(16).padStart(2, "0");

  return `#${rHex}${gHex}${bHex}`;
};

// mapbox-gl's default (unstyled by this project) popup chrome that sits outside the measured
// content element: .mapboxgl-popup-content padding (10px top + 15px bottom) plus the
// .mapboxgl-popup-tip arrow (10px). None of this is captured by the content div's own
// offsetHeight, so it's added on top of every measurement below.
const MAPBOX_POPUP_CHROME_HEIGHT = 75;

/**
 * Measures the tooltip height of a selected county via the shared `resizeObserverManager` and reports it through `setSelectedTooltipHeight`,
 * so the map can center on the rendered tooltip instead of the county's center.
 */
const createTooltipHeightObserver = (
  setSelectedTooltipHeight: (value: number | null) => void,
): (element: HTMLDivElement | null) => void => {
  let observedElement: HTMLDivElement | null = null;

  return (element: HTMLDivElement | null) => {
    if (observedElement === element) return;

    if (observedElement) {
      resizeObserverManager.unobserve(observedElement);
    }

    observedElement = element;

    if (element) {
      resizeObserverManager.observe(element, (entry) => setSelectedTooltipHeight(entry.contentRect.height + MAPBOX_POPUP_CHROME_HEIGHT));
      setSelectedTooltipHeight(element.offsetHeight + MAPBOX_POPUP_CHROME_HEIGHT);
    } else {
      setSelectedTooltipHeight(null);
    }
  };
};

export {
  adjustColorBrightness,
  convertFootprintRecordToGeoJson,
  convertToFeatureCollection,
  createTooltipHeightObserver,
  getFeatureClusterBounds,
  getProviderCircleRadius,
};
